/**
 * WHAT A CANDIDATE SEES ABOVE EVERY PORTAL FORM (#372 part 3, Paul 2026-09-29/30).
 *
 * `Form 4 of 22 · Read and sign` — the form's place in their packet, then what kind of thing it is.
 * Read off the frames rather than the ticket text, because Paul updated Figma first: `347:1529`
 * ("Form 1 of 22 · Fill in"), `381:3152` ("Form 5 of 22 · Health choice"), `379:2861` ("Form 4 of
 * 22 · Read and sign") and `388:4107` ("Form 7 of 22 · Review and sign a PDF").
 *
 * IT REPLACES THE OUTSTANDING-WORK COUNTER, and that is a real change rather than a rewording. The
 * old line was `3 of 7` — the candidate's position among what they still had to do, which shrank as
 * they worked. This is an IDENTITY: form 4 is form 4 whether they have finished it or not. The
 * frames show one line, not both, so the progress reading is gone. Anyone restoring it should put
 * it somewhere that cannot be mistaken for this.
 */
/**
 * The kind, as the frames word it — and `null` where no frame says.
 *
 * ONLY FOUR ARE KNOWN, and the rest are deliberately `null` rather than invented. This is the same
 * wall `packetStepCopy` hit at #264 and refused to climb: the frames group forms as "Fill in",
 * "Read and sign", "Health choices", "Review and sign a PDF", "Patient Visit Rules Acknowledgment",
 * "Acknowledge before your first visit" and "Upload", and several of those are not a step type. A
 * `date-credential` or an `info-task` has no frame that names it, so this says nothing about them
 * rather than guessing — the header simply reads `Form 4 of 22` with no suffix.
 *
 * A wrong label is worse than no label here: it is the first thing a candidate reads on a form they
 * are being asked to sign.
 *
 * `satisfies Record<StepTypeId, …>` so a new step type is a compile error rather than a silently
 * unlabelled form, the rule `packetStepCopy` and `lifecyclePresenters` already keep.
 */
export const portalFormKindLabels = {
    /** `347:1529` — "Form 1 of 22 · Fill in". */
    'candidate-application': 'Fill in',
    /** `379:2861` — "Form 4 of 22 · Read and sign". */
    acknowledgment: 'Read and sign',
    /** `388:4107` — "Form 7 of 22 · Review and sign a PDF". */
    'pdf-form': 'Review and sign a PDF',
    /**
     * The 3.1 group heading is "Upload" (`323:1347`), already singular, and no portal frame for an
     * upload has been read. Kept because the group heading IS the per-form label everywhere it has
     * been checked — "Fill in", "Read and sign" and "Review and sign a PDF" all match their group
     * exactly, and "Health choices" singularises to "Health choice".
     */
    'document-upload': 'Upload',
    /** 3.8 and 3.7 sit in the "Health choices" group, which singularises here (#06b form 6). */
    'health-choice': 'Health choice',
    /** No frame names these, so the header says nothing about them. See the note above. */
    'date-credential': null,
    'external-process': null,
    'hr-record': null,
    'info-task': null,
};
/**
 * `Form 4 of 22 · Read and sign`, or `Form 4 of 22` where no frame names the kind.
 *
 * Returns null when the position cannot be worked out — a step that is not in the packet it was
 * drawn from. Printing "Form 0 of 22" or "Form -1 of 22" over somebody's signature page is worse
 * than printing nothing, and the caller already has the form's own heading to show.
 */
export function portalFormHeader({ number, total, type }) {
    if (!Number.isInteger(number) || number < 1 || !Number.isInteger(total) || total < number)
        return null;
    const label = portalFormKindLabels[type] ?? null;
    return label === null ? `Form ${number} of ${total}` : `Form ${number} of ${total} · ${label}`;
}
/**
 * Where this form sits in the packet, 1-based, or null when it is not in it.
 *
 * COUNTED WITHIN WHAT THE PERSON ACTUALLY HAS — Paul's decision, 2026-09-30, asked because the
 * frames could not answer it.
 *
 * His frames number HIPAA as form 10 of 22, drawn against the FINISHED portal. Only 13 of those 22
 * forms have a step definition today, so a candidate's packet is not 22 and does not contain form
 * 10 in his sense. The two ways out were to number within the real packet ("Form 4 of 13", true
 * now, converging on his numbers as the missing forms are built) or to show his fixed numbers
 * ("Form 10 of 22", matching the design but printing 1, 4, 10, 15 … with gaps over a total larger
 * than what was sent). He chose the first.
 *
 * So this needs no migration: as each missing form is built the numbers move toward the frames on
 * their own, and the day all 22 exist it reads exactly as Paul drew it.
 */
/**
 * ONE NUMBERED FORM MAY HOLD SEVERAL STEPS (Julio, 2026-10-05: "yes to the grouping").
 *
 * The design asks for 22 numbered forms where the catalog has 26 definitions: 22 uploads shown as
 * 7, one application shown as 3. So a form is not the same thing as a step, and the number the
 * candidate reads belongs to the FORM. Steps sharing a `portalForm` key are one form and carry one
 * number; a step without the key is its own form, exactly as before.
 *
 * NOT A RENAME OF `portalGroup`, which is a different question and stays. `portalGroup` says which
 * heading a form sits under on 3.1 ("Fill in", "Upload"); `portalForm` says which numbered form a
 * step belongs to. Two steps can share a group and be separate forms, which is the common case.
 *
 * ORDER DECIDES THE NUMBER, as it did before: a form takes the number of its first step in the
 * packet. A later step of the same form does not take a new one, which is the whole point.
 *
 * WITHOUT ANY KEY THE RESULT IS IDENTICAL to the old `indexOf + 1` — there is no migration, and a
 * test pins that, because the day this ships nothing in the catalog carries a key yet.
 */
export function portalFormPosition(packet, stepId) {
    const numbers = formNumbers(packet);
    const step = packet.find((item) => item.id === stepId);
    return step === undefined ? null : numbers.get(formKey(step)) ?? null;
}
/**
 * How many forms the packet holds, which is NOT `packet.length` once anything is grouped.
 *
 * The caller used `forms.length` for the total. Left alone it would say "Form 3 of 13" on a packet
 * of 9 forms — a number the candidate counts against and finds wrong.
 */
export function portalFormCount(packet) {
    return formNumbers(packet).size;
}
/**
 * A step's form identity. Its own id when it names no form, so an ungrouped step cannot collide
 * with another ungrouped one — and the two namespaces are kept apart by the prefix, so a
 * `portalForm` that happens to equal some other step's id does not merge them by accident.
 */
function formKey(step) {
    return step.portalForm == null || step.portalForm === '' ? `step:${step.id}` : `form:${step.portalForm}`;
}
/** Each form's number, in the order the packet first mentions it. */
function formNumbers(packet) {
    const numbers = new Map();
    for (const step of packet) {
        const key = formKey(step);
        if (!numbers.has(key))
            numbers.set(key, numbers.size + 1);
    }
    return numbers;
}
/**
 * HOW 3.1 GROUPS THE PORTAL FORMS (#372 part 3, frame `323:1347`).
 *
 * MODELLED AS DATA BECAUSE IT CANNOT BE DERIVED, and that was checked rather than assumed. Four of
 * these groups are a step type under another name; the other four are not, and nothing else in the
 * catalog stands in for them:
 *
 *   - "Health choices" holds Hep B and influenza. `fileCategory: 'segregated-health'` looked like
 *     the discriminator and is not — the TB rows carry the same category and sit in a DIFFERENT
 *     group.
 *   - "Acknowledge before your first visit" holds the TB baseline and the payroll set-up. Gates
 *     looked like the discriminator and are not — TB gates `independent-service`, payroll gates
 *     nothing at all.
 *
 * That is the same wall `packetStepCopy` recorded at #264, re-tested against the current catalog.
 * Grouping is a DESIGN decision, so it lives as design data rather than as a derivation that would
 * drift the first time either side learned a new rule.
 *
 * ORDER IS THE FRAME'S ORDER, and it is the array's order, so 3.1 renders by iterating this.
 */
export const portalFormGroups = [
    { id: 'fill-in', label: 'Fill in' },
    { id: 'read-and-sign', label: 'Read and sign' },
    { id: 'health-choices', label: 'Health choices' },
    { id: 'review-and-sign-pdf', label: 'Review and sign a PDF' },
    /**
     * A group of ONE, named after its only form. That is what the frame draws, and it is recorded as
     * drawn rather than folded into "Review and sign a PDF" where it would otherwise sit — Paul gave
     * it its own heading on the day he added it, which reads as deliberate prominence. If it turns
     * out to be provisional, this is one line and the form moves.
     */
    { id: 'patient-visit-rules', label: 'Patient Visit Rules Acknowledgment' },
    { id: 'before-first-visit', label: 'Acknowledge before your first visit' },
    { id: 'upload', label: 'Upload' },
    /**
     * The last group's heading carries the form's own number — "Form 22 — added to the portal at
     * orientation" — so it is generated rather than fixed. `portalFormGroupHeading` below builds it;
     * this label is what the group IS, for anywhere that needs to name it without a number.
     */
    { id: 'at-orientation', label: 'Added to the portal at orientation' },
];
/**
 * The heading 3.1 shows above a group.
 *
 * Every group but the last is its plain label. The orientation group names the form number it
 * holds, because a candidate meeting it months later needs to know which form it is, and the frame
 * writes it that way.
 */
export function portalFormGroupHeading(id, numbers) {
    const group = portalFormGroups.find((entry) => entry.id === id);
    if (group === undefined)
        return '';
    if (id !== 'at-orientation' || numbers.length !== 1)
        return group.label;
    return `Form ${numbers[0]} — added to the portal at orientation`;
}
/**
 * THE ONE ORDER BOTH SURFACES USE (#372 part 3).
 *
 * 3.1 lists the forms by group — Fill in, then Read and sign, then Health choices, and so on — and
 * the candidate's "Form 4 of 22" has to be the same 4 that HR sees, or the two screens are talking
 * about different forms while using the same word. So the order is defined once, here, and the
 * server sorts with it before either client sees a packet.
 *
 * Stable within a group: a form keeps the position the packet gave it among its neighbours, so
 * reordering is a change to the group list above and nothing else.
 *
 * A form with no group sorts LAST rather than first. It is the unknown case — a definition added
 * without a group, which a test already fails on — and burying it at the end is better than giving
 * it number 1 and renumbering everything real behind it.
 */
export function orderPortalForms(forms) {
    const rank = new Map(portalFormGroups.map((group, at) => [group.id, at]));
    const of = (form) => rank.get(String(form.portalGroup ?? '')) ?? portalFormGroups.length;
    return [...forms]
        .map((form, at) => ({ form, at }))
        .sort((a, b) => of(a.form) - of(b.form) || a.at - b.at)
        .map((entry) => entry.form);
}
//# sourceMappingURL=form-header.js.map